import type { WeeklyStatus, WeeklyTask, WeeklyUserSummary } from '../../../lib/weeklyApi';
import { TaskListRow, weeklyTaskRow } from '../../tasks/TaskListRow';
import { WEEKLY_STATUS_META } from './weeklyStatusMeta';
import { WeeklyStatusIcon, WeeklyStatusLabel, useWeeklyStatusColor } from './WeeklyStatusVisual';
import { CalendarPlus } from 'lucide-react';
import { formatTransitionDate } from './weeklyWeek';
import { useI18n } from '../../../contexts/I18nContext';

interface Props {
  summary: WeeklyUserSummary;
  /** When non-empty, only these status groups are shown. Empty = show all. */
  visibleStatuses: Set<WeeklyStatus>;
}

// Initials from a display name, e.g. "Andrei Pata" → "AP".
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Per-task status-transition timeline (CLE-74 §1): when the issue was created
 * and the date of each in-week status change, e.g.
 *   Created: 8 Jun · → In Progress: 9 Jun · → Done: 11 Jun
 */
function TaskTransitions({ task }: { task: WeeklyTask }) {
  const { t } = useI18n();
  if (!task.createdAt && task.changes.length === 0) return null;
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
      {task.createdAt && (
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <CalendarPlus size={11} className="text-gray-400" />
          {t('weekly.created')}: {formatTransitionDate(task.createdAt)}
        </span>
      )}
      {task.changes.map((c) => (
        <span key={c.at} className="inline-flex items-center gap-1 whitespace-nowrap">
          → <WeeklyStatusLabel status={c.to} size={11} />: {formatTransitionDate(c.at)}
        </span>
      ))}
    </p>
  );
}

/**
 * One team member's weekly card (CLE-71 §3): avatar / initials, name + changed
 * task count, then their tasks grouped by the status they landed in this week
 * (Done / In Progress / In Review / QA / To Do / Blocked …). Status groups are
 * filtered by `visibleStatuses` when a filter is active.
 */
export function WeeklyUserCard({ summary, visibleStatuses }: Props) {
  const { t } = useI18n();
  const colorOf = useWeeklyStatusColor();
  const { user, groups } = summary;

  const filtered =
    visibleStatuses.size === 0 ? groups : groups.filter((g) => visibleStatuses.has(g.status));

  const shownTotal = filtered.reduce((acc, g) => acc + g.tasks.length, 0);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-3">
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="h-9 w-9 rounded-full object-cover" />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
            {initials(user.name)}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-semibold text-gray-900">{user.name}</p>
          <p className="text-xs text-gray-500">
            {shownTotal === 1
              ? t('weekly.taskChangedOne', { count: shownTotal })
              : t('weekly.taskChangedMany', { count: shownTotal })}
          </p>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-400">{t('weekly.noMatchingChanges')}</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((group) => {
            const meta = WEEKLY_STATUS_META[group.status];
            const color = colorOf(group.status);
            return (
              <div key={group.status}>
                <p
                  className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider"
                  style={{ color }}
                >
                  <WeeklyStatusIcon status={group.status} color={color} size={13} />
                  {t(meta.labelKey)} ({group.tasks.length})
                </p>
                <ul className="mt-1 overflow-hidden rounded-md border border-gray-200">
                  {group.tasks.map((task) => (
                    <TaskListRow key={task.id} task={weeklyTaskRow(task)} hideAssignee>
                      <TaskTransitions task={task} />
                    </TaskListRow>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
